"use client";

import ArrowLeftIcon from "@/components/_icons/arrowLeftIcon";
import { Merchant } from "@/dto/merchant";
import { usePathname, useRouter } from "next/navigation";
import { SimpleStorefrontHours } from "./header";
import { Service } from "@/dto/service";
import { ServiceType } from "@/constants/enum";
import LocalizedPhoneDisplay from "../_localized/localizedPhoneDisplay";
import {
  ROUTE_CHECKOUT,
  ROUTE_CONFIRMATION,
  ROUTE_GUEST_CHECKOUT,
  ROUTE_RETAIL,
  ROUTE_SERVICES,
  ROUTE_HOME,
} from "@/utils/constants";
import MerchantLinkButton from "../merchantLinkButton/merchantLinkButton";
import { useTranslations } from "next-intl";
import { useMerchantColor } from "@/utils/hooks/useMerchantColor";
import { useCheckoutNavigation } from "@/app/providers/checkoutNavigationContext";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import useMediaQuery from "@/utils/useMediaQuery";
import { use } from "react";
import { Surcharge } from "@/components/surcharge/surcharge";

interface DudaHeaderProps {
  merchant: Merchant;
  paymentConfigPromise?: Promise<any>;
}

/**
 * DudaHeader Component
 *
 * Displays a horizontal header with:
 * - Left side: back arrow icon and merchant name
 * - Right side: today's business hours and merchant phone number
 *
 * The component uses provided colors for background and text styling.
 * Business hours are derived from the merchant's data for the current day.
 *
 * Props:
 * - merchant: Merchant data containing name, phone, and business hours
 * - headerBackgroundColor: Background color (default: white)
 * - textColor: Text color (default: black)
 * - onBackClick: Optional callback for back button; uses router.back() by default
 */
export const DudaHeader = ({
  merchant,
  paymentConfigPromise,
}: DudaHeaderProps) => {
  const pathName = usePathname();
  const router = useRouter();
  const t = useTranslations("Header");
  const merchantColor = useMerchantColor();
  const { handleCheckoutBackPress } = useCheckoutNavigation();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const merchantPaymentConfig =
    paymentConfigPromise && use(paymentConfigPromise);
  const dudaHeaderStyles = merchant?.dudaWebsiteData?.headerStyles || {};
  const { bgColor, color } = dudaHeaderStyles;

  const service = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );

  const currentDay = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  const headerBusinessHours = merchant?.isRestaurant
    ? service?.hours
    : merchant?.businessHours?.hours;

  const showBusinessHours = ![
    ROUTE_GUEST_CHECKOUT,
    ROUTE_CHECKOUT,
    ROUTE_CONFIRMATION,
  ].includes(pathName);

  //@ts-ignore
  const todayHours = headerBusinessHours?.[currentDay] ?? [];

  const getMerchantLinkButtonHref = () => {
    return merchant.isRetail ? ROUTE_RETAIL : ROUTE_SERVICES;
  };

  return (
    <div
      className={`flex ${
        isMobileScreen
          ? "flex-col gap-y-6 items-start"
          : "flex-row items-center"
      } w-full justify-between px-4 py-4 md:px-6 clover-header-bg-color clover-header-text-color`}
    >
      {/* Left side: Back arrow and merchant name */}
      <div>
        <button
          type="button"
          className={`flex items-center ${!isMobileScreen ? "p-2" : ""}`}
          aria-label="Go back"
          data-testid={applyDataTestId(pom.header.dudaHeaderBackButton)}
          onClick={(e) => {
            if (pathName === ROUTE_RETAIL) {
              globalThis.location.assign(ROUTE_HOME);
              return;
            }
            if (handleCheckoutBackPress()) {
              e.preventDefault();
              return;
            }
            router.back();
          }}
        >
          <div className="absolute left-4">
            <ArrowLeftIcon />
          </div>
          <h1 className="leading-normal wrap-anywhere break-normal wrap-anywhere sm:max-w-none text-xl pl-6 font-normal line-clamp-2">
            {merchant.name}
          </h1>
        </button>
      </div>
      {showBusinessHours && (
        <div
          className={`flex flex-col ${
            isMobileScreen ? "items-start" : "items-end"
          }`}
        >
          <SimpleStorefrontHours
            merchant={merchant}
            todayHours={todayHours}
            abbreviation={merchant.timezone}
            headerBusinessHours={headerBusinessHours}
            showHoursModal={false}
          />

          <LocalizedPhoneDisplay
            phoneNumber={merchant.phone}
            locale={merchant.locale}
            international
          />

          {merchantPaymentConfig &&
            merchantPaymentConfig.surcharging?.rate > 0 && (
              <Surcharge
                surchargeRate={merchantPaymentConfig.surcharging?.rate}
              />
            )}
        </div>
      )}
      {pathName === ROUTE_CONFIRMATION && (
        <p className="hidden sm:block font-normal text-[15px]">
          <MerchantLinkButton
            merchantColor={color || merchantColor}
            textColor={bgColor}
            uri={getMerchantLinkButtonHref()}
            text={t("keepBrowsing")}
          />
        </p>
      )}
    </div>
  );
};
